import { useState, useEffect, FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../services/firebase";
import {
  loginWithEmail,
  resetPasswordEmail,
  changePassword,
} from "../services/auth.service";
import "./login.css";

/* ================= TYPES ================= */

type ViewType = "login" | "forceReset" | "forgotPw";

type Testimonial = {
  name: string;
  role: string;
  text: string;
  avatar: string;
};

type Props = {
  forceReset?: boolean;
};

/* ================= CONSTANTS ================= */

const STATS = [
  { value: "12,400+", label: "Active Users" },
  { value: "98.6%", label: "Accuracy" },
  { value: "3.2s", label: "Response" },
  { value: "40+", label: "Countries" },
];

const FEATURES = [
  {
    icon: "👁️",
    title: "Real-Time Eye Tracking",
    desc: "Monitor eye movement frequency live via Bluetooth.",
  },
  {
    icon: "📡",
    title: "Focus Zone Detection",
    desc: "Detect drift from 90–100 Hz range instantly.",
  },
  {
    icon: "🎧",
    title: "Smart Audio Response",
    desc: "Trigger calming audio when focus drops.",
  },
  {
    icon: "📊",
    title: "Waveform Analytics",
    desc: "Visualize Hz history with min/avg/max stats.",
  },
];

const TESTIMONIALS: Testimonial[] = [
  {
    name: "Arjun R.",
    role: "Software Engineer",
    text: "Focus Vision completely changed how I work.",
    avatar: "AR",
  },
  {
    name: "Sneha M.",
    role: "UX Researcher",
    text: "Like having a window into my own concentration.",
    avatar: "SM",
  },
  {
    name: "Daniel K.",
    role: "Medical Student",
    text: "The audio cue snaps me back instantly.",
    avatar: "DK",
  },
];

/* ================= COMPONENT ================= */

export default function LoginPage({ forceReset = false }: Props) {
  const navigate = useNavigate();

  const [view, setView] = useState<ViewType>(
    forceReset ? "forceReset" : "login"
  );

  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");

  const [newPassword, setNewPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");

  const [loggedInUser, setLoggedInUser] = useState<any>(null);

  const [resetEmail, setResetEmail] = useState<string>("");
  const [resetSent, setResetSent] = useState<boolean>(false);

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  const [showPw, setShowPw] = useState<boolean>(false);
  const [visible, setVisible] = useState<boolean>(false);

  const [testimonialIdx, setTestimonialIdx] = useState<number>(0);

  /* ================= EFFECTS ================= */

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const t = setInterval(
      () =>
        setTestimonialIdx((i) => (i + 1) % TESTIMONIALS.length),
      4000
    );
    return () => clearInterval(t);
  }, []);

  /* ================= HANDLERS ================= */

  const handleLogin = async (e: FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      setError("Enter email and password.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const cred: any = await loginWithEmail(email, password);
      const user = cred.user;

      const snap = await getDoc(doc(db, "users", user.uid));

      if (snap.exists() && snap.data().forcePasswordReset === true) {
        setLoggedInUser(user);
        setView("forceReset");
      } else {
        navigate("/home");
      }
    } catch (err) {
      handleAuthError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleForceReset = async (e: FormEvent) => {
    e.preventDefault();

    if (newPassword.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      await changePassword(newPassword);

      await updateDoc(doc(db, "users", loggedInUser.uid), {
        forcePasswordReset: false,
      });

      navigate("/home");
      window.location.reload();
    } catch {
      setError("Failed to update password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: FormEvent) => {
    e.preventDefault();

    if (!resetEmail) {
      setError("Enter your email address.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      await resetPasswordEmail(resetEmail);
      setResetSent(true);
    } catch (err: any) {
      setError(
        err?.code === "auth/user-not-found"
          ? "No account found with this email."
          : "Failed to send reset email."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleAuthError = (err: any) => {
    const map: Record<string, string> = {
      "auth/user-not-found": "No account found with this email.",
      "auth/wrong-password": "Incorrect password.",
      "auth/invalid-email": "Invalid email format.",
      "auth/too-many-requests": "Too many attempts. Try again later.",
      "auth/network-request-failed": "Network error.",
      "auth/invalid-credential": "Invalid email or password.",
    };

    setError(map[err?.code] || "Sign-in failed. Please try again.");
  };

  const t = TESTIMONIALS[testimonialIdx];

  /* ================= JSX ================= */

  return (
    <div className={`lp-root ${visible ? "lp-visible" : ""}`}>
      <div className="lp-layout">
        {/* LEFT PANEL */}
        <div className="lp-left">
          <h1>FocusVision</h1>
        </div>

        {/* RIGHT PANEL */}
        <div className="lp-right">
          <div className="lp-card">

            {/* LOGIN */}
            {view === "login" && (
              <>
                <h2>Login</h2>

                <form onSubmit={handleLogin}>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Email"
                  />

                  <input
                    type={showPw ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Password"
                  />

                  {error && <p>{error}</p>}

                  <button type="submit" disabled={loading}>
                    {loading ? "Signing in..." : "Sign In"}
                  </button>
                </form>
              </>
            )}

            {/* FORCE RESET */}
            {view === "forceReset" && (
              <>
                <h2>Reset Password</h2>

                <form onSubmit={handleForceReset}>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="New Password"
                  />

                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm Password"
                  />

                  {error && <p>{error}</p>}

                  <button type="submit" disabled={loading}>
                    {loading ? "Updating..." : "Update Password"}
                  </button>
                </form>
              </>
            )}

            {/* FORGOT */}
            {view === "forgotPw" && (
              <>
                <h2>Forgot Password</h2>

                <form onSubmit={handleForgotPassword}>
                  <input
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    placeholder="Email"
                  />

                  {error && <p>{error}</p>}

                  <button type="submit" disabled={loading}>
                    {loading ? "Sending..." : "Send Reset"}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}